<script setup lang="ts">
import { computed, ref, watch, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { FileService } from '@/services/file.services';
import { useBodyScrollLock } from '@/composables/useBodyScrollLock';

const props = defineProps<{
  open: boolean;
  file: any | null;
  restaurants?: any[];
}>();
const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'deleted'): void;
  (e: 'uploaded'): void;
}>();

const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

useBodyScrollLock(toRef(props, 'open'));

const isUploadMode = computed(() => !props.file);

function fileStoreId(f: any) {
  return f?.restaurant?.id ?? f?.restaurantId ?? null;
}
function findRestaurant(id: string | null | undefined) {
  if (!id) return null;
  if (props.file?.restaurant?.id === id) return props.file.restaurant;
  return (props.restaurants ?? []).find((r: any) => r.id === id) ?? null;
}
function restaurantName(id: string | null | undefined) {
  if (!id) return '—';
  const r = findRestaurant(id);
  return r ? (r.designation ?? r.name ?? id) : id;
}
function restaurantLogo(id: string | null | undefined) {
  const r = findRestaurant(id);
  return r?.logoUrl ?? null;
}

function fileThumb(f: any) {
  return f?.info?.secureUrl ?? f?.info?.url ?? null;
}
function fileName(f: any) {
  return f?.designation || f?.info?.publicId || t('medias.untitled');
}

// ── Suppression (fichier existant) ─────────────────────────────────────────────
const isDeleting = ref(false);
const deleteError = ref('');
const confirmDeleteOpen = ref(false);

async function confirmDelete() {
  if (!props.file?.id) return;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    await FileService.deleteFileById(props.file.id);
    confirmDeleteOpen.value = false;
    emit('deleted');
    emit('close');
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    deleteError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('medias.deleteError');
  } finally {
    isDeleting.value = false;
  }
}

function goToStore(storeId: string | undefined) {
  if (!storeId) return;
  emit('close');
  router.push(`/commerces/${storeId}?tab=medias`);
}

// ── Actions rapides (voir / copier) ────────────────────────────────────────────
const copied = ref(false);

function viewFile(f: any) {
  const url = fileThumb(f);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}
async function copyFileUrl(f: any) {
  const url = fileThumb(f);
  if (!url) return;
  try {
    await navigator.clipboard.writeText(url);
    copied.value = true;
    setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    // clipboard indisponible : ignoré silencieusement
  }
}

// ── Upload (nouveau fichier) ────────────────────────────────────────────────────
const uploadStoreId = ref('');
const uploadDesignation = ref('');
const uploadFile = ref<File | null>(null);
const uploadPreview = ref<string | null>(null);
const isUploading = ref(false);
const uploadError = ref('');
const uploadSuccess = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen && isUploadMode.value) {
      uploadStoreId.value = '';
      uploadDesignation.value = '';
      uploadFile.value = null;
      uploadPreview.value = null;
      uploadError.value = '';
      uploadSuccess.value = false;
    }
    if (isOpen && !isUploadMode.value) {
      confirmDeleteOpen.value = false;
      deleteError.value = '';
    }
  },
);

function triggerFilePicker() {
  fileInput.value?.click();
}
function onFileSelected(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (!file) return;
  uploadFile.value = file;
  if (!uploadDesignation.value) uploadDesignation.value = file.name;
  const reader = new FileReader();
  reader.onload = (ev) => {
    uploadPreview.value = ev.target?.result as string;
  };
  reader.readAsDataURL(file);
}

async function submitUpload() {
  if (
    !uploadFile.value ||
    !uploadStoreId.value ||
    !uploadDesignation.value.trim()
  ) {
    uploadError.value = t('medias.uploadMissingFields');
    return;
  }
  isUploading.value = true;
  uploadError.value = '';
  try {
    await FileService.uploadImageById({
      file: uploadFile.value,
      designation: uploadDesignation.value.trim(),
      restaurantId: uploadStoreId.value,
    });
    uploadSuccess.value = true;
    emit('uploaded');
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    uploadError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('medias.uploadError');
  } finally {
    isUploading.value = false;
  }
}

function close() {
  emit('close');
}
</script>

<template>
  <Teleport to="body">
    <Transition name="md-backdrop">
      <div v-if="open" class="md-backdrop" @click="close" />
    </Transition>
    <Transition name="md-drawer">
      <aside v-if="open" class="md-drawer" @click.stop>
        <div class="md">
          <!-- Mode détail -->
          <template v-if="!isUploadMode">
            <div class="md__header">
              <button class="md__back" @click="close">
                <i class="pi pi-times" />
              </button>
              <div class="md__header-info">
                <h1 class="md__name">{{ fileName(file) }}</h1>
              </div>
            </div>

            <div class="md__card">
              <div class="md__thumb">
                <img
                  v-if="fileThumb(file)"
                  :src="fileThumb(file)"
                  :alt="fileName(file)"
                  loading="lazy"
                />
                <i v-else class="pi pi-image" />
              </div>
              <div class="md__thumb-actions">
                <button
                  class="md__btn md__btn--ghost md__btn--sm"
                  @click="viewFile(file)"
                >
                  <i class="pi pi-eye" /> {{ t('medias.view') }}
                </button>
                <button
                  class="md__btn md__btn--ghost md__btn--sm"
                  @click="copyFileUrl(file)"
                >
                  <i :class="copied ? 'pi pi-check' : 'pi pi-copy'" />
                  {{ copied ? t('medias.copied') : t('medias.copyLink') }}
                </button>
              </div>
            </div>

            <div class="md__card">
              <div class="md__card-hd">
                <h3 class="md__card-title">
                  <i class="pi pi-shop" /> {{ t('medias.associatedStore') }}
                </h3>
              </div>
              <div class="md__store-row">
                <div class="md__store-logo">
                  <img
                    v-if="restaurantLogo(fileStoreId(file))"
                    :src="restaurantLogo(fileStoreId(file))!"
                    :alt="restaurantName(fileStoreId(file))"
                    loading="lazy"
                  />
                  <i v-else class="pi pi-shop" />
                </div>
                <span class="md__store-name">{{
                  restaurantName(fileStoreId(file))
                }}</span>
              </div>
              <div class="md__info-view">
                <div class="md__info-row">
                  <span class="md__info-label">{{ t('medias.format') }}</span>
                  <span class="md__info-value">{{
                    file?.moreInfo?.format ?? '—'
                  }}</span>
                </div>
                <div class="md__info-row">
                  <span class="md__info-label">{{
                    t('medias.dimensions')
                  }}</span>
                  <span class="md__info-value">
                    {{ file?.moreInfo?.width ?? '—' }} ×
                    {{ file?.moreInfo?.height ?? '—' }}
                  </span>
                </div>
              </div>
              <div class="md__actions">
                <button
                  class="md__btn md__btn--ghost"
                  :disabled="!fileStoreId(file)"
                  @click="goToStore(fileStoreId(file))"
                >
                  <i class="pi pi-arrow-right" /> {{ t('medias.viewStore') }}
                </button>
              </div>
            </div>

            <div class="md__card md__danger-card">
              <div class="md__card-hd">
                <h3 class="md__card-title md__card-title--danger">
                  <i class="pi pi-exclamation-triangle" />
                  {{ t('medias.dangerZone') }}
                </h3>
              </div>
              <div class="md__edit-body">
                <p v-if="deleteError" class="md__error">
                  <i class="pi pi-exclamation-circle" /> {{ deleteError }}
                </p>
                <div v-if="!confirmDeleteOpen" class="md__actions">
                  <button
                    class="md__btn md__btn--danger"
                    @click="confirmDeleteOpen = true"
                  >
                    <i class="pi pi-trash" /> {{ t('medias.delete') }}
                  </button>
                </div>
                <div v-else class="md__confirm-delete">
                  <p class="md__danger-text">{{ t('medias.deleteConfirm') }}</p>
                  <div class="md__actions">
                    <button
                      class="md__btn md__btn--ghost"
                      @click="confirmDeleteOpen = false"
                    >
                      {{ t('medias.cancel') }}
                    </button>
                    <button
                      class="md__btn md__btn--danger"
                      :disabled="isDeleting"
                      @click="confirmDelete"
                    >
                      <span v-if="isDeleting" class="md__spinner" />
                      {{
                        isDeleting
                          ? t('medias.deleting')
                          : t('medias.deleteConfirmBtn')
                      }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Mode upload -->
          <template v-else>
            <div class="md__header">
              <button class="md__back" @click="close">
                <i class="pi pi-times" />
              </button>
              <div class="md__header-info">
                <h1 class="md__name">{{ t('medias.uploadNew') }}</h1>
              </div>
            </div>

            <p v-if="uploadSuccess" class="md__success">
              <i class="pi pi-check-circle" /> {{ t('medias.uploadSuccess') }}
            </p>

            <div v-else class="md__card">
              <div class="md__edit-body">
                <div class="md__upload-zone" @click="triggerFilePicker">
                  <img
                    v-if="uploadPreview"
                    :src="uploadPreview"
                    class="md__upload-preview"
                    alt=""
                  />
                  <template v-else>
                    <i class="pi pi-upload" />
                    <span>{{ t('medias.choosePhoto') }}</span>
                  </template>
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  class="md__file-input"
                  @change="onFileSelected"
                />

                <div class="md__field">
                  <label class="md__label">{{ t('medias.designation') }}</label>
                  <input v-model="uploadDesignation" class="md__input" />
                </div>

                <div class="md__field">
                  <label class="md__label">{{ t('medias.store') }}</label>
                  <select v-model="uploadStoreId" class="md__input">
                    <option value="">{{ t('medias.chooseStore') }}</option>
                    <option
                      v-for="r in restaurants ?? []"
                      :key="r.id"
                      :value="r.id"
                    >
                      {{ r.designation ?? r.name }}
                    </option>
                  </select>
                </div>

                <p v-if="uploadError" class="md__error">
                  <i class="pi pi-exclamation-circle" /> {{ uploadError }}
                </p>

                <div class="md__actions">
                  <button class="md__btn md__btn--ghost" @click="close">
                    {{ t('medias.cancel') }}
                  </button>
                  <button
                    class="md__btn md__btn--primary"
                    :disabled="isUploading"
                    @click="submitUpload"
                  >
                    <span v-if="isUploading" class="md__spinner" />
                    {{
                      isUploading ? t('medias.uploading') : t('medias.upload')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.md-backdrop {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(8, 12, 20, 0.45);
  backdrop-filter: blur(2px);
}
.md-backdrop-enter-active,
.md-backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.md-backdrop-enter-from,
.md-backdrop-leave-to {
  opacity: 0;
}

.md-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 901;
  width: min(480px, 100vw);
  background: #f4f6f9;
  box-shadow: -4px 0 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}
[data-theme='dark'] .md-drawer {
  background: #0c1421;
}
.md-drawer-enter-active,
.md-drawer-leave-active {
  transition: transform 0.25s ease;
}
.md-drawer-enter-from,
.md-drawer-leave-to {
  transform: translateX(100%);
}

.md {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.75rem;
  padding-bottom: 3rem;
  box-sizing: border-box;
  overflow-y: auto;
}

.md__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.md__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.md__header-info {
  min-width: 0;
}
.md__name {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: var(--nxt-on-surface);
}

.md__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  overflow: hidden;
}
.md__card-hd {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.md__card-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.md__card-title--danger {
  color: var(--nxt-error);
}

.md__thumb {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 1.25rem 1.25rem 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-muted);
  font-size: 2rem;
  overflow: hidden;
}
.md__thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.md__thumb-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem;
}

.md__store-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1.25rem 0;
}
.md__store-logo {
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
  font-size: 1.125rem;
}
.md__store-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.md__store-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.md__info-view {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem;
}
.md__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8125rem;
}
.md__info-label {
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.md__info-value {
  color: var(--nxt-on-surface);
  font-weight: 700;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.md__edit-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
}
.md__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.md__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.md__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.md__input:focus {
  border-color: #ffd60a;
}

.md__upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1.5rem;
  min-height: 8rem;
  border-radius: 0.875rem;
  border: 2px dashed var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  overflow: hidden;
}
.md__upload-zone:hover {
  border-color: #ffd60a;
}
.md__upload-zone i {
  font-size: 1.5rem;
}
.md__upload-preview {
  max-width: 100%;
  max-height: 10rem;
  border-radius: 0.5rem;
  object-fit: cover;
}
.md__file-input {
  display: none;
}

.md__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}
.md__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f7b0f;
  background: rgba(16, 181, 16, 0.12);
}

.md__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.md__actions .md__btn {
  flex: 1;
  justify-content: center;
}
.md__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.md__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.md__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}
.md__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.md__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.md__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.md__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  animation: md-spin 0.7s linear infinite;
}
@keyframes md-spin {
  to {
    transform: rotate(360deg);
  }
}

.md__danger-card {
  border-color: rgba(220, 38, 38, 0.3);
}
.md__danger-text {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.md__confirm-delete {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>

<style>
[data-theme='dark'] .md__header,
[data-theme='dark'] .md__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .md__back,
[data-theme='dark'] .md__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .md__input,
[data-theme='dark'] .md__upload-zone {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .md__input:focus,
[data-theme='dark'] .md__upload-zone:hover {
  border-color: #ffd60a;
}
[data-theme='dark'] .md__thumb {
  background: rgba(255, 255, 255, 0.05);
}
</style>
